import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getPlatformAccounts, getGiftCards, addGiftCards, uploadGiftCards, updateGiftCard, deleteGiftCard, getLpgIds, addLpgIds, deleteLpgId, getPaymentCards, addPaymentCard, deletePaymentCard, bulkAddCardsText, bulkAddCardsExcel, downloadCardTemplate, recordCardUsage, startWizard, updateWizardStep, getWizardSessions, getActiveWizard } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Lightning, ArrowSquareOut, CheckCircle, XCircle, Clock, Copy, Gift, Trash, UploadSimple, DownloadSimple, CreditCard, ListNumbers, Play, ArrowRight, ArrowClockwise } from '@phosphor-icons/react';
import { TotpDisplay } from '../components/TotpDisplay';
import SemiAutoTab from '../components/quick-booking/SemiAutoTab';
import { toast } from 'sonner';

const PROVIDERS = {
  indane: { name: 'Indane Gas', color: 'bg-orange-500', code: 'INDI00000NATT5' },
  bharat: { name: 'Bharat Gas', color: 'bg-blue-600', code: 'BPCLGAS00000AI' },
  hp: { name: 'HP Gas', color: 'bg-red-600', code: 'HPCLLPGGAS' },
};
const AMAZON_URLS = { login: 'https://www.amazon.in/ap/signin', topup: 'https://www.amazon.in/apay/detail/gc-top-up', lpg_base: 'https://www.amazon.in/apay/interstitial/lpg/' };
const STEPS = ['login', 'topup', 'bill_fetch', 'payment'];
const STEP_LABELS = { login: 'Login', topup: 'Balance Top-Up', bill_fetch: 'Bill Fetch', payment: 'Payment' };

export default function QuickBookingPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState(() => {
    if (typeof window !== 'undefined') {
      const q = new URLSearchParams(window.location.search).get('tab');
      if (q) return q;
    }
    return 'wizard';
  });

  // Data
  const [accounts, setAccounts] = useState([]);
  const [columns, setColumns] = useState([]);
  const [lpgIds, setLpgIds] = useState([]);
  const [cards, setCards] = useState([]);
  const [giftCards, setGiftCards] = useState({ cards: [], total: 0, unused: 0, used: 0, failed: 0 });
  const [wizardHistory, setWizardHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Wizard state
  const [wizardActive, setWizardActive] = useState(false);
  const [wizardSession, setWizardSession] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedLpgId, setSelectedLpgId] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('indane');
  const [selectedCard, setSelectedCard] = useState('');
  const [billAmount, setBillAmount] = useState('');

  // Form states
  const [lpgIdsText, setLpgIdsText] = useState('');
  const [lpgProvider, setLpgProvider] = useState('indane');
  const [cardPasteText, setCardPasteText] = useState('');
  const [gcPasteText, setGcPasteText] = useState('');
  const [gcFilter, setGcFilter] = useState('');
  const [gcSearch, setGcSearch] = useState('');

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Copied!'); };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [accRes, lpgRes, cardRes, gcRes, wizRes] = await Promise.all([
        getPlatformAccounts({ platform: 'amazon' }),
        getLpgIds(),
        getPaymentCards(),
        getGiftCards(gcFilter ? { status: gcFilter } : {}),
        getWizardSessions(),
      ]);
      setAccounts((accRes.data?.accounts || [])); setColumns(accRes.data?.columns || []);
      setLpgIds(lpgRes.data || []);
      setCards(cardRes.data || []);
      setGiftCards(gcRes.data || { cards: [], total: 0, unused: 0, used: 0, failed: 0 });
      setWizardHistory(wizRes.data || []);
    } catch (err) { toast.error('Load failed'); }
    finally { setLoading(false); }
  }, [gcFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Check for active wizard on load
  useEffect(() => {
    (async () => {
      try {
        const res = await getActiveWizard();
        if (res.data?.session) {
          setWizardSession(res.data.session);
          setWizardActive(true);
          toast.info('Active session found — Resume kar sakte ho');
        }
      } catch (err) { console.error('Card usage record failed'); }
    })();
  }, []);

  // ─── Wizard Handlers ───
  const handleStartWizard = async () => {
    if (!selectedAccount || !selectedLpgId) { toast.error('Account aur LPG ID select karo'); return; }
    try {
      const res = await startWizard({ account_id: selectedAccount, lpg_id: selectedLpgId, provider: selectedProvider, card_id: selectedCard });
      const activeRes = await getActiveWizard();
      if (activeRes.data?.session) {
        setWizardSession(activeRes.data.session);
        setWizardActive(true);
        toast.success('Wizard started!');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Start failed'); }
  };

  const handleStepDone = async (step) => {
    if (!wizardSession) return;
    const payload = { step, status: 'done' };
    if (step === 'bill_fetch' && billAmount) payload.bill_amount = parseFloat(billAmount);
    try {
      await updateWizardStep(wizardSession.id, payload);
      // If payment done + card selected, record usage
      if (step === 'payment' && wizardSession.card_id && wizardSession.bill_amount > 0) {
        try { await recordCardUsage({ card_id: wizardSession.card_id, amount: wizardSession.bill_amount, note: `LPG ${wizardSession.lpg_id}` }); } catch (err) { console.error('Card usage failed'); }
      }
      if (step === 'payment') {
        setWizardActive(false); setWizardSession(null); setBillAmount('');
        toast.success('Booking Complete!');
        fetchAll();
      } else {
        const activeRes = await getActiveWizard();
        if (activeRes.data?.session) setWizardSession(activeRes.data.session);
      }
    } catch (err) { toast.error('Update failed'); }
  };

  const handleStepFail = async (step) => {
    if (!wizardSession) return;
    try {
      await updateWizardStep(wizardSession.id, { step, status: 'failed' });
      setWizardActive(false); setWizardSession(null);
      toast.error('Marked as failed');
      fetchAll();
    } catch (err) { toast.error('Update failed'); }
  };

  const handleResume = () => { setTab('wizard'); };

  // ─── Data Handlers ───
  const handleAddLpgIds = async () => {
    if (!lpgIdsText.trim()) return;
    try {
      const res = await addLpgIds({ ids_text: lpgIdsText, provider: lpgProvider });
      toast.success(`${res.data.added} LPG IDs added${res.data.skipped ? ` (${res.data.skipped} duplicate)` : ''}`);
      setLpgIdsText(''); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAddCards = async () => {
    if (!cardPasteText.trim()) { toast.error('Card details paste karo'); return; }
    try {
      const res = await bulkAddCardsText({ text: cardPasteText });
      toast.success(`${res.data.added} cards added${res.data.skipped ? ` (${res.data.skipped} duplicate)` : ''}`);
      setCardPasteText(''); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleCardExcel = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await bulkAddCardsExcel(fd);
      toast.success(`${res.data.added} cards added from Excel`);
      e.target.value = ''; fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
  };

  const handleDownloadCardTemplate = async () => {
    try {
      const res = await downloadCardTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'payment_cards_template.xlsx'; a.click();
    } catch (err) { toast.error('Download failed'); }
  };

  const handleAddGc = async () => {
    if (!gcPasteText.trim()) return;
    const codes = gcPasteText.split(/[\n,;]+/).map(s => s.trim()).filter(s => s.length >= 4);
    try {
      const res = await addGiftCards(codes);
      toast.success(`${res.data.added} added`);
      setGcPasteText(''); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleGcUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const r = await uploadGiftCards(fd); toast.success(`${r.data.added} added`); e.target.value=''; fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);
  const unusedGc = giftCards.cards?.filter(c => c.status === 'unused') || [];
  const activeCard = cards.find(c => c.id === (wizardSession?.card_id || selectedCard));
  const activeAccount = accounts.find(a => a.id === (wizardSession?.account_id || selectedAccount));

  if (user?.role !== 'admin' && user?.role !== 'owner') return <div className="p-6 text-center text-gray-500">Admin Only</div>;

  return (
    <div className="space-y-4" data-testid="quick-booking-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Lightning size={22} weight="fill" className="text-amber-500" /> Quick Booking</h1>
          <p className="text-xs text-gray-500">Semi-Automated LPG Payment — Step by Step</p>
        </div>
        {wizardActive && tab !== 'wizard' && (
          <Button className="bg-amber-500 hover:bg-amber-600 gap-1.5 animate-pulse" onClick={handleResume} data-testid="resume-btn">
            <ArrowClockwise size={16} weight="bold" /> Resume Booking
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex bg-gray-100 p-0.5 rounded-lg gap-0.5 overflow-x-auto">
        {[
          { key: 'wizard', label: 'Booking Wizard', icon: <Play size={14} /> },
          { key: 'semi-auto', label: 'Semi-Auto', icon: <Lightning size={14} /> },
          { key: 'lpg-ids', label: `LPG IDs (${lpgIds.length})`, icon: <ListNumbers size={14} /> },
          { key: 'cards', label: `Cards (${cards.length})`, icon: <CreditCard size={14} /> },
          { key: 'gift-cards', label: `Gift Cards (${giftCards.unused})`, icon: <Gift size={14} /> },
          { key: 'history', label: 'History', icon: <Clock size={14} /> },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-1 px-3 py-2 rounded text-xs font-medium whitespace-nowrap transition ${tab === t.key ? 'bg-white shadow text-blue-700' : 'text-gray-500'}`}>{t.icon} {t.label}</button>
        ))}
      </div>

      {/* ═══ WIZARD TAB ═══ */}
      {tab === 'wizard' && (
        wizardActive && wizardSession ? (
          <WizardActiveView session={wizardSession} cards={cards} activeCard={activeCard} accounts={accounts} onStepDone={handleStepDone} onStepFail={handleStepFail} onStop={() => { handleStepFail(wizardSession.current_step); }} copy={copy} billAmount={billAmount} setBillAmount={setBillAmount} unusedGc={unusedGc} formatCurrency={formatCurrency} handleGcMark={async (id, s) => { try { await updateGiftCard(id, { status: s }); fetchAll(); } catch (e) { console.error('GC mark failed:', e); } }} />
        ) : (
          <div className="space-y-4">
            <div className="bg-white border rounded-lg p-4 space-y-4">
              <h2 className="font-bold text-sm">Start New Booking</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Amazon Account</Label>
                  <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                    <SelectTrigger className="mt-1" data-testid="wizard-account"><SelectValue placeholder="Select account" /></SelectTrigger>
                    <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{Object.values(a.data || {})[0] || a.id}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">LPG ID</Label>
                  <Select value={selectedLpgId} onValueChange={setSelectedLpgId}>
                    <SelectTrigger className="mt-1" data-testid="wizard-lpg"><SelectValue placeholder="Select LPG ID" /></SelectTrigger>
                    <SelectContent>{lpgIds.map(l => <SelectItem key={l.id} value={l.lpg_id}>{l.lpg_id} {l.customer_name && `(${l.customer_name})`}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Provider</Label>
                  <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(PROVIDERS).map(([k, v]) => <SelectItem key={k} value={k}>{v.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Payment Card (optional)</Label>
                  <Select value={selectedCard} onValueChange={setSelectedCard}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select card" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value=" ">None</SelectItem>
                      {cards.map(c => <SelectItem key={c.id} value={c.id}>****{c.last4} ({formatCurrency(c.remaining)} left)</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 gap-2" onClick={handleStartWizard} disabled={!selectedAccount || !selectedLpgId} data-testid="start-wizard-btn">
                <Play size={16} weight="fill" /> Start Booking Wizard
              </Button>
            </div>
          </div>
        )
      )}

      {/* ═══ SEMI-AUTO TAB ═══ */}
      {tab === 'semi-auto' && <SemiAutoTab />}

      {/* ═══ LPG IDs TAB ═══ */}
      {tab === 'lpg-ids' && (
        <div className="space-y-4">
          <div className="bg-white border rounded-lg p-4 space-y-3">
            <Label className="text-sm font-bold">Add LPG IDs (line by line)</Label>
            <div className="flex gap-2 items-end">
              <Select value={lpgProvider} onValueChange={setLpgProvider}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PROVIDERS).map(([k, v]) => <SelectItem key={k} value={k}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Textarea value={lpgIdsText} onChange={e => setLpgIdsText(e.target.value)} placeholder={"LPG IDs paste karo:\n9631623572\n9835344589"} rows={3} className="font-mono text-xs" data-testid="lpg-ids-input" />
            <Button className="bg-blue-600 hover:bg-blue-700 gap-1" onClick={handleAddLpgIds} data-testid="add-lpg-ids"><ListNumbers size={14} /> Add IDs</Button>
          </div>
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="overflow-y-auto max-h-[400px]">
              <Table>
                <TableHeader><TableRow className="bg-gray-50"><TableHead className="text-xs">#</TableHead><TableHead className="text-xs">LPG ID</TableHead><TableHead className="text-xs">Provider</TableHead><TableHead className="text-xs">Last Status</TableHead><TableHead className="text-xs">Last Booked</TableHead><TableHead className="text-xs w-16"></TableHead></TableRow></TableHeader>
                <TableBody>
                  {lpgIds.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-gray-400">No LPG IDs</TableCell></TableRow> : lpgIds.map((l, i) => (
                    <TableRow key={l.id}>
                      <TableCell className="text-xs text-gray-400">{i + 1}</TableCell>
                      <TableCell className="font-mono text-sm font-bold">{l.lpg_id}</TableCell>
                      <TableCell className="text-xs">{PROVIDERS[l.provider]?.name || l.provider}</TableCell>
                      <TableCell>{l.last_status ? <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${l.last_status === 'booked' ? 'bg-green-100 text-green-700' : 'bg-gray-100'}`}>{l.last_status}</span> : '—'}</TableCell>
                      <TableCell className="text-xs">{l.last_booked_at || '—'}</TableCell>
                      <TableCell><button onClick={() => deleteLpgId(l.id).then(fetchAll)} className="text-red-400 hover:text-red-600"><Trash size={14} /></button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      )}

      {/* ═══ CARDS TAB ═══ */}
      {tab === 'cards' && (
        <div className="space-y-4">
          <div className="bg-white border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-bold">Add Cards — Paste Raw Text</Label>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="text-xs gap-1 h-7" onClick={handleDownloadCardTemplate} data-testid="card-template-btn"><DownloadSimple size={12} /> Template</Button>
                <label className="cursor-pointer"><Button size="sm" variant="outline" className="text-xs gap-1 h-7" asChild><span><UploadSimple size={12} /> Excel Upload</span></Button><input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleCardExcel} data-testid="card-excel-upload" /></label>
              </div>
            </div>
            <Textarea value={cardPasteText} onChange={e => setCardPasteText(e.target.value)} placeholder={"Jaise hai waise paste karo (multiple cards bhi):\n\n5321 3502 6358 5572\nCVV2 - 924\nExpiry - 04/26 (mm/yy)\nCard Limit Rs.18510\n\n4111 2222 3333 4444\nCVV - 123\nExpiry - 12/27\nCard Limit Rs.25000"} rows={6} className="font-mono text-xs" data-testid="card-paste-input" />
            <Button className="bg-blue-600 hover:bg-blue-700 gap-1" onClick={handleAddCards} disabled={!cardPasteText.trim()} data-testid="add-cards-btn"><CreditCard size={14} /> Add Cards</Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {cards.map(c => (
              <div key={c.id} className="bg-white border rounded-lg p-4 space-y-2 relative" data-testid={`card-${c.id}`}>
                <button onClick={() => deletePaymentCard(c.id).then(fetchAll)} className="absolute top-2 right-2 text-red-400 hover:text-red-600"><Trash size={14} /></button>
                <p className="text-xs text-gray-400">{c.label || 'Payment Card'}</p>
                <p className="font-mono text-lg font-bold tracking-wider cursor-pointer" onClick={() => copy(c.card_number)}>{c.card_number.replace(/(.{4})/g, '$1 ').trim()}</p>
                <div className="flex gap-4 text-xs">
                  <span className="cursor-pointer" onClick={() => copy(c.cvv)}>CVV: <b className="font-mono">{c.cvv}</b></span>
                  <span>Exp: <b className="font-mono">{c.expiry}</b></span>
                </div>
                <div className="flex justify-between text-xs pt-1 border-t">
                  <span>Limit: <b>{formatCurrency(c.card_limit)}</b></span>
                  <span>Used: <b className="text-red-600">{formatCurrency(c.used_amount)}</b></span>
                  <span className={`font-bold ${c.remaining > 0 ? 'text-green-600' : 'text-red-600'}`}>Left: {formatCurrency(c.remaining)}</span>
                </div>
              </div>
            ))}
            {cards.length === 0 && <p className="text-gray-400 text-sm col-span-3 text-center py-6">No cards added</p>}
          </div>
        </div>
      )}

      {/* ═══ GIFT CARDS TAB ═══ */}
      {tab === 'gift-cards' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[{ l: 'Total', v: giftCards.total, f: '' }, { l: 'Unused', v: giftCards.unused, f: 'unused', c: 'text-emerald-700' }, { l: 'Used', v: giftCards.used, f: 'used', c: 'text-blue-700' }, { l: 'Failed', v: giftCards.failed, f: 'failed', c: 'text-red-700' }].map(s => (
              <div key={s.l} className="bg-white border rounded-lg p-3 cursor-pointer hover:shadow" onClick={() => setGcFilter(s.f)}>
                <p className="text-[10px] text-gray-400">{s.l}</p><p className={`text-xl font-bold ${s.c || ''}`}>{s.v}</p>
              </div>
            ))}
          </div>
          <div className="bg-white border rounded-lg p-4 space-y-3">
            <Label className="text-sm font-bold">Add Gift Cards</Label>
            <Textarea value={gcPasteText} onChange={e => setGcPasteText(e.target.value)} placeholder="Gift card codes (1 per line)..." rows={3} className="font-mono text-xs" />
            <div className="flex gap-2">
              <Button className="bg-blue-600 hover:bg-blue-700 gap-1" onClick={handleAddGc}><Gift size={14} /> Add</Button>
              <label className="cursor-pointer"><Button size="sm" variant="outline" className="gap-1" asChild><span><UploadSimple size={14} /> Excel</span></Button><input type="file" accept=".xlsx,.xls,.csv,.txt" className="hidden" onChange={handleGcUpload} /></label>
            </div>
          </div>
          {/* Quick dispenser */}
          {unusedGc.length > 0 && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-sm"><Gift size={16} weight="fill" className="text-amber-600 inline mr-1" />Next Gift Card</h3>
                <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded font-bold">{unusedGc.length} left</span>
              </div>
              <div className="bg-white rounded-lg p-3 border mb-2 flex items-center justify-between">
                <p className="font-mono text-xl font-bold select-all">{unusedGc[0]?.code}</p>
                <Button className="bg-blue-600 hover:bg-blue-700 gap-1" onClick={() => copy(unusedGc[0]?.code)}><Copy size={16} /> COPY</Button>
              </div>
              <div className="flex gap-2">
                <Button className="bg-green-600 hover:bg-green-700 gap-1 flex-1" onClick={() => updateGiftCard(unusedGc[0]?.id, { status: 'used' }).then(fetchAll)}><CheckCircle size={14} /> Used</Button>
                <Button className="bg-red-600 hover:bg-red-700 gap-1 flex-1" onClick={() => updateGiftCard(unusedGc[0]?.id, { status: 'failed' }).then(fetchAll)}><XCircle size={14} /> Failed</Button>
              </div>
            </div>
          )}
          <div className="bg-white border rounded-lg overflow-hidden">
            {/* Search bar + filter chip */}
            <div className="border-b bg-slate-50 px-3 py-2 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-600">🔍</span>
              <input
                data-testid="gc-search-input"
                type="text"
                value={gcSearch}
                onChange={(e) => setGcSearch(e.target.value)}
                placeholder="Search by code (e.g. NNPG, 7YMX, H84Q)..."
                className="flex-1 min-w-[200px] border rounded px-2 py-1 text-xs font-mono focus:outline-none focus:border-blue-500"
              />
              {gcFilter && (
                <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                  Filter: {gcFilter}
                  <button onClick={() => setGcFilter('')} className="ml-1 text-blue-600 hover:text-blue-900" data-testid="gc-clear-filter">✕</button>
                </span>
              )}
              {gcSearch && (
                <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">
                  {(giftCards.cards || []).filter(c => c.code?.toLowerCase().includes(gcSearch.toLowerCase())).length} matches
                  <button onClick={() => setGcSearch('')} className="ml-1 text-emerald-700 hover:text-emerald-900" data-testid="gc-clear-search">✕</button>
                </span>
              )}
            </div>
            <div className="overflow-y-auto max-h-[300px]">
              <Table>
                <TableHeader><TableRow className="bg-gray-50"><TableHead className="text-xs py-1 px-2">#</TableHead><TableHead className="text-xs py-1 px-2">Code</TableHead><TableHead className="text-xs py-1 px-2">Status</TableHead><TableHead className="text-xs py-1 px-2">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(() => {
                    const q = gcSearch.trim().toLowerCase();
                    const filtered = q
                      ? (giftCards.cards || []).filter(c => (c.code || '').toLowerCase().includes(q))
                      : (giftCards.cards || []);
                    if (filtered.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-xs text-gray-400 py-6">
                            {q ? `"${gcSearch}" ke liye koi card nahi mila` : 'Koi gift card nahi'}
                          </TableCell>
                        </TableRow>
                      );
                    }
                    return filtered.map((c, i) => (
                      <TableRow key={c.id} className={c.status === 'unused' ? '' : c.status === 'used' ? 'bg-blue-50/30' : 'bg-red-50/30'}>
                        <TableCell className="text-[10px] py-1 px-2">{i + 1}</TableCell>
                        <TableCell className="py-1 px-2 font-mono text-xs cursor-pointer" onClick={() => copy(c.code)}>
                          {q ? (
                            <HighlightedCode code={c.code} query={q} />
                          ) : c.code}
                        </TableCell>
                        <TableCell className="py-1 px-2"><span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${c.status === 'unused' ? 'bg-emerald-100 text-emerald-700' : c.status === 'used' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>{c.status}</span></TableCell>
                        <TableCell className="py-1 px-2"><button onClick={() => deleteGiftCard(c.id).then(fetchAll)} className="text-red-400 hover:text-red-600"><Trash size={12} /></button></TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      )}

      {/* ═══ HISTORY TAB ═══ */}
      {tab === 'history' && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b"><h2 className="text-sm font-bold">Booking History</h2></div>
          <div className="overflow-y-auto max-h-[500px]">
            <Table>
              <TableHeader><TableRow className="bg-gray-50"><TableHead className="text-xs">Date</TableHead><TableHead className="text-xs">LPG ID</TableHead><TableHead className="text-xs">Provider</TableHead><TableHead className="text-xs">Steps</TableHead><TableHead className="text-xs">Amount</TableHead><TableHead className="text-xs">Status</TableHead><TableHead className="text-xs">By</TableHead></TableRow></TableHeader>
              <TableBody>
                {wizardHistory.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-400">No history</TableCell></TableRow> : wizardHistory.map(s => (
                  <TableRow key={s.id}>
                    <TableCell className="text-xs">{s.created_at}</TableCell>
                    <TableCell className="font-mono text-sm font-bold">{s.lpg_id}</TableCell>
                    <TableCell className="text-xs">{s.provider_name}</TableCell>
                    <TableCell>
                      <div className="flex gap-0.5">
                        {STEPS.map(st => {
                          const status = s.steps?.[st]?.status;
                          return <span key={st} className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold ${status === 'done' ? 'bg-green-500 text-white' : status === 'failed' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-500'}`}>{st[0].toUpperCase()}</span>;
                        })}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm font-mono">{s.bill_amount ? formatCurrency(s.bill_amount) : '—'}</TableCell>
                    <TableCell><span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${s.final_status === 'completed' ? 'bg-green-100 text-green-700' : s.final_status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{s.final_status}</span></TableCell>
                    <TableCell className="text-xs">{s.started_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Highlight matched substring in a gift card code ───
function HighlightedCode({ code, query }) {
  if (!query) return code;
  const c = code || '';
  const idx = c.toLowerCase().indexOf(query);
  if (idx < 0) return c;
  return (
    <>
      {c.slice(0, idx)}
      <mark className="bg-yellow-200 text-black font-bold rounded px-0.5">{c.slice(idx, idx + query.length)}</mark>
      {c.slice(idx + query.length)}
    </>
  );
}


// ─── Wizard Active View Component ───
function WizardActiveView({ session, cards, activeCard, accounts, onStepDone, onStepFail, onStop, copy, billAmount, setBillAmount, unusedGc, formatCurrency, handleGcMark }) {
  const currentStep = session.current_step;
  const provider = PROVIDERS[session.provider] || PROVIDERS.indane;
  const providerUrl = `${AMAZON_URLS.lpg_base}${provider.code}`;

  // Find account OTP secret
  const otpKey = Object.keys(session.account_data || {}).find(k => k.toLowerCase().includes('otp') || k.toLowerCase().includes('totp') || k.toLowerCase().includes('secret'));
  const otpVal = otpKey ? session.account_data[otpKey] : null;
  const passwordKey = Object.keys(session.account_data || {}).find(k => k.toLowerCase().includes('pass') || k.toLowerCase().includes('pwd'));
  const passwordVal = passwordKey ? session.account_data[passwordKey] : null;

  return (
    <div className="space-y-4">
      {/* Progress Bar */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-bold">LPG: <span className="font-mono text-blue-600">{session.lpg_id}</span> | {provider.name}</span>
          <Button size="sm" variant="outline" className="text-xs text-red-600" onClick={onStop}>Stop</Button>
        </div>
        <div className="flex gap-1">
          {STEPS.map((st, i) => {
            const status = session.steps?.[st]?.status;
            const isCurrent = st === currentStep;
            return (
              <div key={st} className="flex-1">
                <div className={`h-2 rounded-full ${status === 'done' ? 'bg-green-500' : isCurrent ? 'bg-blue-500 animate-pulse' : 'bg-gray-200'}`} />
                <p className={`text-[10px] mt-1 text-center font-medium ${isCurrent ? 'text-blue-600' : status === 'done' ? 'text-green-600' : 'text-gray-400'}`}>{STEP_LABELS[st]}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Account Info — always visible */}
      <div className="bg-white border rounded-lg p-3">
        <p className="text-[10px] text-gray-400 mb-1">Account Details (click to copy)</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {Object.entries(session.account_data || {}).map(([k, v]) => (
            <div key={k} className="bg-gray-50 rounded px-2 py-1.5 cursor-pointer hover:bg-blue-50" onClick={() => v && copy(String(v))}>
              <p className="text-[9px] text-gray-400 uppercase">{k}</p>
              <p className="font-mono text-xs font-bold truncate">{v || '—'}</p>
            </div>
          ))}
        </div>
        {otpVal && otpVal !== '-' && (
          <div className="mt-2 p-2 bg-slate-50 border rounded">
            <TotpDisplay secret={otpVal} label="Amazon OTP" />
          </div>
        )}
      </div>

      {/* Current Step Card */}
      <div className="bg-white border-2 border-blue-400 rounded-lg p-4 shadow">
        {currentStep === 'login' && (
          <div className="space-y-3">
            <h3 className="font-bold text-base flex items-center gap-2">Step 1: Amazon Login</h3>
            <p className="text-xs text-gray-500">Amazon pe login karo. Account details upar hain — click karke copy karo.</p>
            {passwordVal && <Button variant="outline" className="gap-1 text-xs" onClick={() => copy(String(passwordVal))}><Copy size={14} /> Copy Password</Button>}
            <div className="flex gap-2">
              <Button className="bg-orange-500 hover:bg-orange-600 gap-1 flex-1" onClick={() => window.open(AMAZON_URLS.login, '_blank')}><ArrowSquareOut size={16} /> Open Amazon Login</Button>
            </div>
            <div className="flex gap-2 pt-2 border-t">
              <Button className="bg-green-600 hover:bg-green-700 gap-1 flex-1" onClick={() => onStepDone('login')}><CheckCircle size={16} /> Login Done <ArrowRight size={14} /></Button>
              <Button variant="outline" className="text-red-600" onClick={() => onStepFail('login')}><XCircle size={14} /> Fail</Button>
            </div>
          </div>
        )}

        {currentStep === 'topup' && (
          <div className="space-y-3">
            <h3 className="font-bold text-base">Step 2: Balance Top-Up (Gift Card)</h3>
            <p className="text-xs text-gray-500">Amazon Pay balance mein gift card se top-up karo</p>
            <Button className="bg-orange-500 hover:bg-orange-600 gap-1 w-full" onClick={() => window.open(AMAZON_URLS.topup, '_blank')}><ArrowSquareOut size={16} /> Open Amazon Top-Up Page</Button>
            {/* Gift Card Dispenser */}
            {unusedGc.length > 0 ? (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                <p className="text-[10px] text-amber-600 mb-1">Gift Card ({unusedGc.length} left)</p>
                <div className="flex items-center justify-between">
                  <p className="font-mono text-lg font-bold select-all">{unusedGc[0]?.code}</p>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 gap-1" onClick={() => copy(unusedGc[0]?.code)}><Copy size={14} /> Copy</Button>
                </div>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 gap-1 flex-1" onClick={() => handleGcMark(unusedGc[0]?.id, 'used')}>Used</Button>
                  <Button size="sm" className="bg-red-600 hover:bg-red-700 gap-1 flex-1" onClick={() => handleGcMark(unusedGc[0]?.id, 'failed')}>Failed</Button>
                </div>
              </div>
            ) : <p className="text-xs text-red-500">No unused gift cards. Gift Cards tab mein add karo.</p>}
            {/* Card details for payment */}
            {activeCard && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-[10px] text-blue-600 mb-1">Payment Card</p>
                <p className="font-mono text-sm font-bold cursor-pointer" onClick={() => copy(activeCard.card_number)}>{activeCard.card_number.replace(/(.{4})/g, '$1 ')}</p>
                <div className="flex gap-3 text-xs mt-1">
                  <span className="cursor-pointer" onClick={() => copy(activeCard.cvv)}>CVV: <b>{activeCard.cvv}</b></span>
                  <span>Exp: <b>{activeCard.expiry}</b></span>
                  <span className="text-green-600">Left: <b>{formatCurrency(activeCard.remaining)}</b></span>
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-2 border-t">
              <Button className="bg-green-600 hover:bg-green-700 gap-1 flex-1" onClick={() => onStepDone('topup')}><CheckCircle size={16} /> Top-Up Done <ArrowRight size={14} /></Button>
              <Button variant="outline" className="gap-1" onClick={() => onStepDone('topup')}>Skip</Button>
              <Button variant="outline" className="text-red-600" onClick={() => onStepFail('topup')}><XCircle size={14} /></Button>
            </div>
          </div>
        )}

        {currentStep === 'bill_fetch' && (
          <div className="space-y-3">
            <h3 className="font-bold text-base">Step 3: LPG Bill Fetch</h3>
            <p className="text-xs text-gray-500">LPG provider page pe jaao → LPG ID auto-copy se daal do → Bill fetch hoga</p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-[10px] text-emerald-600">LPG ID (click to copy)</p>
              <p className="font-mono text-xl font-bold cursor-pointer" onClick={() => copy(session.lpg_id)}>{session.lpg_id}</p>
            </div>
            <Button className="bg-orange-500 hover:bg-orange-600 gap-1 w-full" onClick={() => { copy(session.lpg_id); window.open(providerUrl, '_blank'); }}><ArrowSquareOut size={16} /> Copy LPG ID & Open {provider.name}</Button>
            <div>
              <Label className="text-xs">Bill Amount (dikhne ke baad daalo)</Label>
              <Input type="number" value={billAmount} onChange={e => setBillAmount(e.target.value)} placeholder="e.g. 917" className="mt-1 font-mono" data-testid="bill-amount" />
            </div>
            <div className="flex gap-2 pt-2 border-t">
              <Button className="bg-green-600 hover:bg-green-700 gap-1 flex-1" onClick={() => onStepDone('bill_fetch')}><CheckCircle size={16} /> Bill Fetched <ArrowRight size={14} /></Button>
              <Button variant="outline" className="text-red-600" onClick={() => onStepFail('bill_fetch')}><XCircle size={14} /></Button>
            </div>
          </div>
        )}

        {currentStep === 'payment' && (
          <div className="space-y-3">
            <h3 className="font-bold text-base">Step 4: Payment</h3>
            <p className="text-xs text-gray-500">Amazon pe payment karo — Pay click karo</p>
            {activeCard && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-[10px] text-blue-600 mb-1">Card for Payment (click to copy)</p>
                <p className="font-mono text-sm font-bold cursor-pointer" onClick={() => copy(activeCard.card_number)}>{activeCard.card_number.replace(/(.{4})/g, '$1 ')}</p>
                <div className="flex gap-3 text-xs mt-1">
                  <span className="cursor-pointer hover:text-blue-700" onClick={() => copy(activeCard.cvv)}>CVV: <b>{activeCard.cvv}</b></span>
                  <span>Exp: <b>{activeCard.expiry}</b></span>
                </div>
              </div>
            )}
            {session.bill_amount > 0 && <p className="text-sm">Amount: <b className="font-mono text-lg">{formatCurrency(session.bill_amount)}</b></p>}
            <div className="flex gap-2 pt-2 border-t">
              <Button className="bg-green-600 hover:bg-green-700 gap-1 flex-1 text-base py-3" onClick={() => onStepDone('payment')}><CheckCircle size={18} weight="fill" /> Payment Done!</Button>
              <Button variant="outline" className="text-red-600" onClick={() => onStepFail('payment')}><XCircle size={14} /> Fail</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
